import React from "react";
import { useTranslation } from "react-i18next";
import { useSelectedHotelId } from "@/utils/hotelContext";
import { getHttpStatus } from "@/utils/httpErrors";
import { PageState } from "@/components/common/PageState";
import { useCommunicationIntegrations } from "./hooks/communication-integrations.hooks";
import { IntegrationsList } from "./components/IntegrationsList";

export const CommunicationIntegrationsPage = (): React.JSX.Element => {
  const { t } = useTranslation();
  const hotelId = useSelectedHotelId();
  const { data, isLoading, isError, error } =
    useCommunicationIntegrations(hotelId);

  if (!hotelId) {
    return <PageState text={t("communicationChannels.page.noHotelSelected")} />;
  }

  if (isLoading) {
    return <PageState text={t("communicationChannels.page.loading")} />;
  }

  if (isError) {
    if (getHttpStatus(error) === 503) {
      return (
        <div className="flex flex-col gap-6 p-12 max-w-4xl">
          <header className="flex flex-col gap-2">
            <h1 className="text-2xl font-semibold text-foreground">
              {t("communicationChannels.page.title")}
            </h1>
            <p className="text-sm text-muted-foreground max-w-2xl">
              {t("communicationChannels.page.description")}
            </p>
          </header>
          <div className="rounded-md border border-destructive/40 bg-destructive/5 p-4 text-sm text-destructive">
            {t("communicationChannels.killSwitch")}
          </div>
        </div>
      );
    }
    return (
      <PageState
        text={t("communicationChannels.page.loadError")}
        tone="error"
      />
    );
  }

  return (
    <div className="flex flex-col gap-6 p-12 max-w-4xl">
      <header className="flex flex-col gap-2">
        <h1 className="text-2xl font-semibold text-foreground">
          {t("communicationChannels.page.title")}
        </h1>
        <p className="text-sm text-muted-foreground max-w-2xl">
          {t("communicationChannels.page.description")}
        </p>
      </header>

      <IntegrationsList hotelId={hotelId} integrations={data ?? []} />
    </div>
  );
};
